import { observer } from "mobx-react-lite";
import { useContext } from "react";

import RunButton from "../../../ui/RunButton";
import { usePlayground } from "../../playground-store";
import { PlaygroundDialogType } from "../../PlaygroundDialog/constants";
import { PlaygroundDialogStoreContext } from "../../PlaygroundDialog/playground-dialog-store";
import TourDisabledArea from "../../tour/TourDisabledArea";
import TourTargetContainer from "../../tour/TourTargetContainer";

import { VariableSetSelector } from "@/components/Playground/Messages/MessagesHeader/VariableSetSelector";
import { PlaygroundModeSwitcher } from "@/components/Playground/Drawer/ControlBar/PlaygroundModeSwitcher";
import { IS_RECAPTCHA_ENABLED, IS_RECAPTCHA_ENABLED_PLAYGROUND } from "@/constants";
import { useUser } from "@/context/user-context";
import { useRecaptcha } from "@/providers/GoogleReCaptchaProvider/hooks/useRecaptcha";
import { useOptionalPlaygroundTourContext } from "@/components/Playground/tour/context";
import { TOUR_TARGET_IDS } from "@/components/Playground/tour/constants";
import { PLAYGROUND_DRAWER_MODES } from "@/components/Playground/types";

interface ControlBarProps {
  collapsed?: boolean;
  onRequestExpandForRun?: () => void;
}

const ControlBar = ({ collapsed = false, onRequestExpandForRun }: ControlBarProps) => {
  const playground = usePlayground();
  const playgroundDialogStore = useContext(PlaygroundDialogStoreContext);
  const user = useUser();
  const { executeRecaptcha } = useRecaptcha();
  const tour = useOptionalPlaygroundTourContext();

  const handleRun = async () => {
    if (
      playground.input_variables.length === 0 &&
      playgroundDialogStore &&
      !localStorage.getItem("showInputVariablesTutorial") &&
      user.isNewUser
    ) {
      playgroundDialogStore.setIsDialogOpen({
        isOpen: true,
        dialogType: PlaygroundDialogType.NoInputVariables,
      });
      return;
    }

    let recaptcha_response = "recaptcha-disabled";
    if (IS_RECAPTCHA_ENABLED && IS_RECAPTCHA_ENABLED_PLAYGROUND && executeRecaptcha) {
      recaptcha_response = await executeRecaptcha("playground");
    }

    playground.run(recaptcha_response);
  };

  const isEvaluateDrawer =
    playground.drawerMode === PLAYGROUND_DRAWER_MODES.EVALUATE && !playground.isSessionModeChat;
  const shouldShowRunButton = !playground.isSessionModeChat && !isEvaluateDrawer;
  const outputCount = playground.outputs.length + playground.pendingOutputs;
  const inputSetCount = Math.max(playground.input_variable_sets.length, 1);
  const inputSetNoun = inputSetCount === 1 ? "input set" : "input sets";
  const outputRunLabel = `Run ${inputSetCount} ${inputSetNoun}`;
  const outputRerunLabel = `Run ${inputSetCount === 1 ? "this input set" : "these input sets"} again`;
  const activeInputSetIndex = playground.input_variable_sets.findIndex(
    inputSet => inputSet.id === playground.activeInputVariableSetId,
  );
  const chatMessageCount = playground.chatStore.messageCount(Math.max(activeInputSetIndex, 0));

  const handleCollapsedRunClick = async () => {
    onRequestExpandForRun?.();
    await handleRun();
  };

  if (collapsed) {
    return (
      <div className="flex h-full w-full shrink-0 items-center justify-between gap-3 px-4 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
          <PlaygroundModeSwitcher
            className="shrink-0"
            outputCount={outputCount}
            chatCount={chatMessageCount}
          />
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {!isEvaluateDrawer && (
            <div className="min-w-0 max-w-[220px] overflow-hidden">
              <VariableSetSelector />
            </div>
          )}
          {shouldShowRunButton && (
            <RunButton
              run={() => {
                void handleCollapsedRunClick();
              }}
              disabled={playground.isAIPromptAssistantLoading}
              disabledTooltip="Save your template first before running"
              isRunning={playground.operationStore.isRunning}
              buttonText={{ idle: outputRunLabel, running: "Running..." }}
              tooltipText={{ idle: outputRunLabel, running: outputRerunLabel }}
              tooltipSide="bottom"
              compact
              allowRunWhileRunning
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full shrink-0 items-center justify-between gap-3 px-4 py-2">
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-left">
        <PlaygroundModeSwitcher
          className="shrink-0"
          outputCount={outputCount}
          chatCount={chatMessageCount}
        />
      </div>

      <div className="flex shrink-0 flex-row items-center gap-x-2">
        {!isEvaluateDrawer && (
          <div className="min-w-0 max-w-[240px] overflow-hidden">
            <VariableSetSelector />
          </div>
        )}
        {shouldShowRunButton && (
          <TourTargetContainer id={TOUR_TARGET_IDS.RUN_BUTTON} className="relative isolate">
            <RunButton
              cancel={playground.cancel}
              run={handleRun}
              disabled={playground.isAIPromptAssistantLoading}
              disabledTooltip="Save your template first before running"
              isRunning={playground.operationStore.isRunning}
              buttonText={{ idle: outputRunLabel, running: "Running..." }}
              tooltipText={{ idle: outputRunLabel, running: outputRerunLabel }}
              tooltipSide="bottom"
              allowRunWhileRunning
            />
            {tour?.isActive && <TourDisabledArea className="z-30" />}
          </TourTargetContainer>
        )}
      </div>
    </div>
  );
};

export default observer(ControlBar);
